import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { MapItem } from '@capturum/ui/api';
import { CommonAnimations } from '@shared/animations/common.animations';
import { BehaviorSubject, EMPTY, Observable, of, Subscription } from 'rxjs';
import { ProductConfigurationFacade } from '@features/client/order-workflow/facades/product-configuration.facade';
import { debounceTime, filter, first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ProductGroupMapItem, ProductTypeMapItem } from '@features/client/order-workflow/models/product.model';
import { BaseDataValueMapItem } from '@core/models/base-data-value-map-item.model';
import { Tooth } from '@features/client/order-workflow/models/tooth.model';
import { RotaryDialButton } from '@features/client/order-workflow/models/rotary-dial-button.model';
import { ToothPart } from '@features/client/order-workflow/enums/tooth-part.enum';
import { ModuleKeys } from '@core/enums/module-keys.enum';
import { ConfirmService } from '@core/services/confirm.service';
import { OrderProduct } from '@features/client/order-workflow/models/order-product.model';
import { flatten, isNil } from 'lodash';
import { FormControl, FormsModule } from '@angular/forms';
import { ToothCrossService } from '@features/client/order-workflow/services/tooth-cross.service';
import { AsyncPipe } from '@angular/common';
import { AttributeBaseDataPipe } from '@shared/pipes/attribute-base-data.pipe';
import { RadioButton } from 'primeng/radiobutton';

@Component({
  selector: 'app-accordion-menu-input',
  templateUrl: './accordion-menu-input.component.html',
  styleUrls: ['./accordion-menu-input.component.scss'],
  animations: [CommonAnimations.detailExpand],
  imports: [AsyncPipe, AttributeBaseDataPipe, RadioButton, FormsModule],
})
export class AccordionMenuInputComponent extends FieldType implements OnInit, AfterViewInit, OnDestroy {
  public items$: BehaviorSubject<MapItem[]> = new BehaviorSubject<MapItem[]>([]);
  public activeAccordionIndex: number;
  public selectedItem: ProductTypeMapItem;
  public orderProduct: OrderProduct;
  public readonly$: Observable<boolean>;
  public isProductCategoryField: boolean;
  /** CSS-based hiding — unlike field.hide, this does NOT destroy the component. */
  public isHiddenByBreadcrumb: boolean = false;
  private selectedAccordion: number;
  private accordionSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private subscription: Subscription = new Subscription();
  private showProductAsRoot: boolean = false;
  private readonly navigationTime: number = 300;
  private readonly defaultSelectedIndexes: any = {
    product_category_id: null,
    product_id: 0,
  };

  constructor(
    public readonly productConfigurationFacade: ProductConfigurationFacade,
    private readonly toothCrossService: ToothCrossService,
    private readonly confirmService: ConfirmService,
    private cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
  ) {
    super();
  }

  public get productCategoryIdForm(): FormControl {
    return this.form.get('product_category_id') as FormControl;
  }

  public ngOnInit(): void {
    this.isProductCategoryField = this.key === 'product_category_id';
    this.readonly$ = this.toothCrossService.getReadonly().pipe(
      filter((readonly) => {
        return !isNil(readonly);
      }),
    );

    this.setDefaultSelectedIndex();
    this.setRootTypes();
    this.handleShowBreadcrumb();

    this.subscription = this.accordionSubject$
      .asObservable()
      .pipe(debounceTime(this.navigationTime))
      .subscribe((index) => {
        this.activeAccordionIndex = index;

        this.cdr.detectChanges();
      });
  }

  public ngAfterViewInit(): void {
    this.fetchItems();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public openDropdown(index: number): void {
    const openAccordion = this.accordionSubject$.getValue();

    if (openAccordion === index) {
      this.accordionSubject$.next(null);

      return;
    }

    this.accordionSubject$.next(index);
  }

  public handleItemClick(item: MapItem, index: number): void {
    this.selectedAccordion = index;

    this.formControl.setValue(item);
  }

  public handleBreadcrumbClick(): void {
    this.productConfigurationFacade.setClickBreadcrumb(true);
  }

  private fetchItems(): void {
    if (this.isProductCategoryField) {
      this.productConfigurationFacade
        .getProductCategories()
        .pipe(first())
        .subscribe((items) => {
          return this.items$.next(items);
        });
    } else {
      this.handleProductTypeChange();
      this.handleProductCategoryChange();
    }
  }

  private handleProductTypeChange(): void {
    this.subscription.add(
      this.formControl?.valueChanges.pipe(filter(Boolean)).subscribe((productType: ProductTypeMapItem) => {
        this.productConfigurationFacade.setProductType(productType);
        this.productConfigurationFacade.setProductTypeClicked(true);

        if (productType.product.is_applicable_to_jaw) {
          this.showJawTypeSelection();
        }
      }),
    );
  }

  private showJawTypeSelection(): void {
    this.productConfigurationFacade.getJawTypes().subscribe((jawTypes: MapItem[]) => {
      this.confirmService.confirm({
        title: `${ModuleKeys.dental}.jaw_type_selection.title`,
        options: jawTypes,
        accept: (value: string) => {
          return console.log('selected jaw type', value);
        },
        reject: () => {
          this.productConfigurationFacade.setProductType(null);
          this.productConfigurationFacade.setSelectedTooth(null);
          this.toothCrossService.clearSelectedTeeth();
        },
      });
    });
  }

  private setRootTypes(): void {
    this.subscription.add(
      this.productConfigurationFacade
        .getShowRoot()
        .pipe(
          switchMap((showRoot) => {
            return showRoot
              ? this.productConfigurationFacade.getRootTypes().pipe(
                  map((rootTypes: BaseDataValueMapItem[]) => {
                    return rootTypes.map((rootType: BaseDataValueMapItem) => {
                      return {
                        tooltipText: rootType.label,
                        onClick: (tooth: Tooth) => {
                          return this.handleSelectedRoot(tooth, rootType);
                        },
                      } as RotaryDialButton;
                    });
                  }),
                )
              : EMPTY;
          }),
        )
        .subscribe((rootTypes: RotaryDialButton[]) => {
          if (this.showProductAsRoot && this.productConfigurationFacade.hasProductType()) {
            rootTypes.unshift(this.convertCrownProductToRootProduct());
          }

          this.productConfigurationFacade.setRootTypes(rootTypes);
        }),
    );
  }

  private handleProductCategoryChange(): void {
    if (!this.productCategoryIdForm) {
      return;
    }

    this.subscription.add(
      this.productCategoryIdForm.valueChanges
        .pipe(
          startWith(this.productCategoryIdForm.value),
          filter((value) => {
            return !isNil(value);
          }),
          switchMap((productCategory: MapItem) => {
            this.productConfigurationFacade.setProductType(null);
            this.productConfigurationFacade.setSelectedTooth(null);
            this.toothCrossService.clearSelectedTeeth();

            return productCategory?.value && typeof productCategory?.value === 'string'
              ? this.productConfigurationFacade.getProductGroupsByCategory(productCategory.value)
              : of([]);
          }),
        )
        .subscribe((items) => {
          this.items$.next(items);
          this.cdr.detectChanges();
        }),
    );
  }

  private handleSelectedRoot(tooth: Tooth, rootType: BaseDataValueMapItem): void {
    const orderProduct = this.productConfigurationFacade.getOrderProductByToothPart(tooth, ToothPart.root);

    if (orderProduct) {
      orderProduct.product_id = rootType.value;

      this.subscription.add(
        this.productConfigurationFacade.createOrderProduct(orderProduct, false).subscribe(() => {
          setTimeout(() => {
            return this.productConfigurationFacade.setSelectedTooth(null);
          });
        }),
      );
    }
  }

  private convertCrownProductToRootProduct(): RotaryDialButton {
    const productType = this.productConfigurationFacade.getProductTypeSnapshot();
    const rootType = {
      children: [],
      parent_id: null,
      label: productType?.label,
      value: productType?.product_id,
      value_key: productType?.value_key,
    };

    return {
      tooltipText: productType?.label,
      onClick: (tooth: Tooth) => {
        return this.handleSelectedRoot(tooth, rootType);
      },
    };
  }

  private handleShowBreadcrumb(): void {
    let wasEverShown = false;

    this.subscription.add(
      this.productConfigurationFacade.isBreadcrumbShown().subscribe((show) => {
        if (show) {
          wasEverShown = true;
        }

        if (!show && wasEverShown && this.isProductCategoryField && !this.selectedItem) {
          this.productConfigurationFacade.setOrderProduct(null);
        }

        const orderProduct = this.productConfigurationFacade.getOrderProductSnapshot();

        if (orderProduct && !this.selectedItem) {
          if (this.isProductCategoryField) {
            this.formControl.setValue(orderProduct.product?.productCategory?.id, { emitEvent: false });
          } else {
            (this.productCategoryIdForm?.value && typeof this.productCategoryIdForm.value === 'string'
              ? this.productConfigurationFacade.getProductGroupsByCategory(this.productCategoryIdForm.value)
              : of([])
            )
              .pipe(
                first(),
                tap((items: ProductGroupMapItem[]) => {
                  const products = flatten(
                    items.map((item) => {
                      return item.children;
                    }),
                  );

                  this.selectedItem = products.find((item) => {
                    return item.value === orderProduct.product?.productType?.id;
                  });

                  this.productConfigurationFacade.setProductType(this.selectedItem);
                }),
              )
              .subscribe((items) => {
                return this.items$.next(items);
              });
          }
        }

        // Use CSS-based hiding instead of field.hide to prevent Formly from
        // destroying and recreating this component. field.hide in Formly v6+
        // destroys the component, which breaks all BehaviorSubject subscriptions.
        this.isHiddenByBreadcrumb = show && this.isProductCategoryField;
        this.toggleFormlyFieldVisibility(!this.isHiddenByBreadcrumb);
      }),
    );
  }

  private setDefaultSelectedIndex(): void {
    const key: string = this.key as string;
    const defaultSelectedIndex: number = this.defaultSelectedIndexes[key];

    if (!isNil(defaultSelectedIndex)) {
      this.accordionSubject$ = new BehaviorSubject<any>(defaultSelectedIndex);
    }

    this.activeAccordionIndex = defaultSelectedIndex;
    this.selectedAccordion = defaultSelectedIndex;
  }

  /** Hide/show the closest `formly-field` ancestor so the entire Formly
   *  wrapper collapses — not just the inner component div. */
  private toggleFormlyFieldVisibility(visible: boolean): void {
    const formlyField = this.elementRef.nativeElement.closest('formly-field');

    if (formlyField) {
      formlyField.style.display = visible ? '' : 'none';
    }
  }
}
